import { useCallback, useEffect, useMemo, useState } from 'react';
import { Epic, Task } from '@amzn/awscat-pattern-library-service-model';
import { TiptapOutputType } from '@amzn/awscat-editor';
import { v4 as uuid } from 'uuid';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useContent } from '../v2-hooks/useContent';
import { useOpenInlineEdits } from '../v2-hooks/useOpenInlineEdits';
import {
  listEpics,
  tryAddEpic,
  tryUpdateEpic,
  tryDeleteEpic,
  tryReorderEpics,
} from '../../services/EpicOperations';
import { tryAddTask, tryReorderTasks } from '../../services/TaskOperations';
import { useInlineCommentManager } from '../../contexts/InlineCommentManagerContext';
import { isCommentResolved } from '../v2-common/utils/commentStateUtils';
import { isThreadRoot } from '../../services/InlineCommentManager';

const EPIC_TITLE_PLACEHOLDER = 'Epic title...';
const EPIC_DESCRIPTION_PLACEHOLDER = '';

export interface EpicInlineSuggestionInfo {
  readonly hasTitleInlineSuggestions: boolean;
  readonly hasDescriptionInlineSuggestions: boolean;
}

export interface EpicEditModalState {
  readonly epicId: string;
  readonly title: TiptapOutputType;
  readonly description: TiptapOutputType;
}

export interface PatternEpicEditorViewModel {
  readonly epics: readonly Epic[];
  /** Per-epic inline edits/comments from AI agents, keyed by epic ID. Used to display warning icons. */
  readonly epicInlineSuggestions: ReadonlyMap<string, EpicInlineSuggestionInfo>;
  /** The id of the task that was just added — consumed once by the task row to start in edit mode. */
  readonly newlyAddedTaskId: string | undefined;
  /** Non-undefined when the edit modal is open. */
  readonly editModal: EpicEditModalState | undefined;
  readonly isSaveEnabled: boolean;
  readonly openEditModal: (epicId: string) => void;
  readonly updateEditTitle: (title: TiptapOutputType, isValid: boolean) => void;
  readonly updateEditDescription: (description: TiptapOutputType, isValid: boolean) => void;
  readonly saveEditModal: () => void;
  readonly dismissEditModal: () => void;
  readonly addEpic: () => void;
  readonly deleteEpic: (epicId: string) => void;
  readonly reorderEpics: (newOrderIds: readonly string[]) => void;
  readonly addTaskToEpic: (epicId: string) => void;
  readonly reorderTasksInEpic: (epicId: string, newOrderIds: readonly string[]) => void;
}

export function usePatternEpicEditorViewModel(): PatternEpicEditorViewModel | undefined {
  const service = useContentEditorService();
  const inlineCommentManager = useInlineCommentManager();
  const content = useContent();

  const [newlyAddedTaskId, setNewlyAddedTaskId] = useState<string | undefined>(undefined);
  const [editModal, setEditModal] = useState<EpicEditModalState | undefined>(undefined);
  const [isTitleValid, setIsTitleValid] = useState(true);
  const [isDescriptionValid, setIsDescriptionValid] = useState(true);

  const epics = useMemo(() => (content ? listEpics(content) : []), [content]);

  // Check each epic for inline edits made by AI agents so we can display warning icons.
  const openedEdits = useOpenInlineEdits();

  // Builds a lookup of which epics have AI-authored inline edits on their title/description.
  const computeInlineSuggestionInfo = useCallback(() => {
    const map = new Map<string, EpicInlineSuggestionInfo>();
    if (!openedEdits) return map;

    for (const epic of epics) {
      const epicTitleFilter = { type: 'epic_title', epicId: epic.id } as const;
      const epicDescriptionFilter = { type: 'epic_description', epicId: epic.id } as const;

      const hasTitleInlineEdits =
        service
          .filterInlineEdits(openedEdits.edits, epicTitleFilter)
          .filter((edit) => !edit.resolved).length > 0;
      const hasTitleInlineComments =
        inlineCommentManager
          .filterComments(epicTitleFilter)
          .filter((c) => isThreadRoot(c))
          .filter((c) => !isCommentResolved(c.state)).length > 0;
      const hasDescriptionInlineEdits =
        service
          .filterInlineEdits(openedEdits.edits, epicDescriptionFilter)
          .filter((edit) => !edit.resolved).length > 0;
      const hasDescriptionInlineComments =
        inlineCommentManager
          .filterComments(epicDescriptionFilter)
          .filter((c) => isThreadRoot(c))
          .filter((c) => !isCommentResolved(c.state)).length > 0;

      map.set(epic.id, {
        hasTitleInlineSuggestions: hasTitleInlineEdits || hasTitleInlineComments,
        hasDescriptionInlineSuggestions: hasDescriptionInlineEdits || hasDescriptionInlineComments,
      });
    }
    return map;
  }, [openedEdits, epics, service, inlineCommentManager]);

  const [recomputeTrigger, setRecomputeTrigger] = useState(0);

  useEffect(() => {
    return service.on('changed', () => setRecomputeTrigger((n) => n + 1));
  }, [service]);

  useEffect(() => {
    return inlineCommentManager.on('changed', () => setRecomputeTrigger((n) => n + 1));
  }, [inlineCommentManager]);

  const epicInlineSuggestions = useMemo(
    () => computeInlineSuggestionInfo(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recomputeTrigger invalidates on service/comment events
    [computeInlineSuggestionInfo, recomputeTrigger],
  );

  const openEditModal = useCallback(
    (epicId: string) => {
      const epic = epics.find((e) => e.id === epicId);
      if (!epic) return;
      setEditModal({ epicId, title: epic.title, description: epic.description });
      setIsTitleValid(true);
      setIsDescriptionValid(true);
    },
    [epics],
  );

  const dismissEditModal = useCallback(() => {
    setEditModal(undefined);
  }, []);

  const updateEditTitle = useCallback((title: TiptapOutputType, isValid: boolean) => {
    setEditModal((prev) => (prev ? { ...prev, title } : undefined));
    setIsTitleValid(isValid);
  }, []);

  const updateEditDescription = useCallback((description: TiptapOutputType, isValid: boolean) => {
    setEditModal((prev) => (prev ? { ...prev, description } : undefined));
    setIsDescriptionValid(isValid);
  }, []);

  const saveEditModal = useCallback(() => {
    if (!editModal || !isTitleValid || !isDescriptionValid) return;
    const liveContent = service.content;
    if (!liveContent) return;
    const updated = tryUpdateEpic(liveContent, editModal.epicId, (epic) => ({
      ...epic,
      title: editModal.title,
      description: editModal.description,
    }));
    if (updated) service.stageEdit(updated);
    setEditModal(undefined);
  }, [editModal, isTitleValid, isDescriptionValid, service]);

  const addEpic = useCallback(() => {
    const liveContent = service.content;
    if (!liveContent) return;

    const newEpic = Object.assign(new Epic(), {
      id: uuid(),
      title: EPIC_TITLE_PLACEHOLDER,
      description: EPIC_DESCRIPTION_PLACEHOLDER,
      tasks: [],
    });

    const updated = tryAddEpic(liveContent, newEpic);
    if (!updated) return;
    service.stageEdit(updated);
  }, [service]);

  const deleteEpic = useCallback(
    (epicId: string) => {
      const liveContent = service.content;
      if (!liveContent) return;
      const updated = tryDeleteEpic(liveContent, epicId);
      if (updated) service.stageEdit(updated);
    },
    [service],
  );

  const reorderEpics = useCallback(
    (newOrderIds: readonly string[]) => {
      const liveContent = service.content;
      if (!liveContent) return;
      const updated = tryReorderEpics(liveContent, newOrderIds);
      if (updated) service.stageEdit(updated);
    },
    [service],
  );

  const addTaskToEpic = useCallback(
    (epicId: string) => {
      const liveContent = service.content;
      if (!liveContent) return;

      const newTask = Object.assign(new Task(), {
        id: uuid(),
        title: '',
        description: '',
        skills: '',
      });

      const updated = tryAddTask(liveContent, epicId, newTask);
      if (updated) {
        service.stageEdit(updated);
        setNewlyAddedTaskId(newTask.id);
      }
    },
    [service],
  );

  const reorderTasksInEpic = useCallback(
    (epicId: string, newOrderIds: readonly string[]) => {
      const liveContent = service.content;
      if (!liveContent) return;
      const updated = tryReorderTasks(liveContent, epicId, newOrderIds);
      if (updated) service.stageEdit(updated);
    },
    [service],
  );

  // A parent-teardown render can transiently clear content before this view
  // unmounts; return undefined so the view renders null instead of throwing.
  if (!content) return undefined;

  return {
    epics,
    epicInlineSuggestions: epicInlineSuggestions,
    newlyAddedTaskId,
    editModal,
    isSaveEnabled: isTitleValid && isDescriptionValid,
    openEditModal,
    updateEditTitle,
    updateEditDescription,
    saveEditModal,
    dismissEditModal,
    addEpic,
    deleteEpic,
    reorderEpics,
    addTaskToEpic,
    reorderTasksInEpic,
  };
}
